import './ChangePassword.scss';
import '../../scss/animations/animations.scss';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { Col, Container, Form, Row } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { LoadingButton, toast } from '../../components';
import CheckboxComponent from '../../components/checkboxes/CheckboxComponent';
import { isSSO } from '../../utils';
import { usePasswordValidation } from './../../hooks/usePasswordValidation';

async function updatePassword(data) {
  const onSuccess = () => {
    toast.success('Successfully updated password!');
  };

  axios.defaults.headers.Authorization = `${localStorage.getItem('token')}`;
  if (data.newPassword.firstPassword === data.oldPassword) {
    toast.error('New password must be different from the current password!');
    return false;
  }
  return axios
    .post(`${window._sonar_env.REACT_APP_BASE_API_URL_V2}/authentication/changepassword`, {
      newPassword: data.newPassword.firstPassword,
      oldPassword: data.oldPassword,
    })
    .then(() => {
      onSuccess();
    });
}

function ChangePassword() {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState({
    firstPassword: '',
    secondPassword: '',
  });
  const [resetKey, setResetKey] = useState(0); // Key for forcing reset
  const formRef = useRef(null);

  const [validLength, hasNumber, upperCase, lowerCase, match, specialChar] = usePasswordValidation({
    firstPassword: newPassword.firstPassword,
    secondPassword: newPassword.secondPassword,
  });

  const setFirst = (event) => {
    setNewPassword({ ...newPassword, firstPassword: event.target.value });
  };
  const setSecond = (event) => {
    setNewPassword({ ...newPassword, secondPassword: event.target.value });
  };

  const history = useHistory();

  const onError = (err) => {
    errors.push(err ? err.response.data.message : 'there was an issue resetting your password.');
    toast.error('Failed to update password.');
  };

  const disabled = () => {
    return validLength === true &&
      hasNumber === true &&
      upperCase === true &&
      lowerCase === true &&
      match === true &&
      specialChar === true
      ? false
      : true;
  };

  const callUpdatePassword = async () => {
    try {
      if (oldPassword !== newPassword) {
        await updatePassword({
          oldPassword,
          newPassword,
        });
        // Ensure all fields are cleared after success
        setOldPassword('');
        setNewPassword({ firstPassword: '', secondPassword: '' });
        // Force a re-render by updating `key`
        setResetKey((prevKey) => prevKey + 1);
      } else {
        toast.error('Old password and new password cannot be same.');
      }
    } catch (error) {
      onError();
    }
  };

  if (isSSO()) {
    history.push('/dashboard');
  }

  useEffect(() => {
    const preventActions = (e) => {
      if (e.target.type === 'password') e.preventDefault();
    };

    const form = formRef.current;
    form?.addEventListener('paste', preventActions);
    form?.addEventListener('copy', preventActions);
    form?.addEventListener('cut', preventActions);

    return () => {
      form?.removeEventListener('paste', preventActions);
      form?.removeEventListener('copy', preventActions);
      form?.removeEventListener('cut', preventActions);
    };
  }, []);

  const passwordRules = [
    { label: 'At least 8 characters.', isValid: validLength },
    { label: 'At least one number.', isValid: hasNumber },
    { label: 'At least one upper case letter (A-Z).', isValid: upperCase },
    { label: 'At least one lower case letter (a-z).', isValid: lowerCase },
    { label: 'At least one special character.', isValid: specialChar },
  ];

  return (
    <>
      <Container>
        <Form ref={formRef} key={resetKey} className="change-password-form">
          <Form.Group>
            <Row>
              <Col>
                <h2>Change Password</h2>

                <p className="mt-4">Passwords must contain:</p>

                {passwordRules.map((rule, index) => (
                  <div key={index} className="d-flex position-relative">
                    {rule.isValid && <div className="primary-checkmark draw me-4"></div>}
                    <Form.Text className={rule.isValid ? 'crossedOutCompleted' : ''}>
                      {rule.label}
                    </Form.Text>
                  </div>
                ))}
              </Col>
              <Col>
                <Form.Control
                  className="mb-4"
                  placeholder="Current Password"
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                />

                <div className="d-flex position-relative">
                  <Form.Control
                    className="mb-4"
                    placeholder="New Password"
                    type="password"
                    onChange={setFirst}
                  />
                  <span className="checks d-flex flex-row mt-2 position-absolute">
                    <CheckboxComponent
                      checked={validLength && hasNumber && upperCase && lowerCase && specialChar}
                    />
                  </span>
                </div>

                <div className="d-flex position-relative">
                  <Form.Control
                    className="mb-2"
                    placeholder="Confirm New Password"
                    type="password"
                    onChange={setSecond}
                  />
                  <span className="checks d-flex flex-row mt-2 position-absolute">
                    <CheckboxComponent
                      checked={
                        newPassword.firstPassword !== '' &&
                        validLength &&
                        hasNumber &&
                        upperCase &&
                        lowerCase &&
                        specialChar &&
                        match
                      }
                    />
                  </span>
                  {newPassword.secondPassword !== '' && !match && (
                    <span className="d-flex flex-row mt-2">
                      <div className="circle-anim danger">
                        <div className="x-mark draw"></div>
                      </div>
                    </span>
                  )}
                </div>

                <small
                  className={`pw-error ${
                    newPassword.secondPassword !== '' && !match ? '' : 'errorHidden'
                  }`}
                >
                  Passwords do not match.
                </small>

                <LoadingButton
                  text="Change My Password"
                  isLoading={false}
                  className="my-3"
                  id="update-pw"
                  onClick={callUpdatePassword}
                  disabled={disabled()}
                />
              </Col>
            </Row>
          </Form.Group>
        </Form>
      </Container>
    </>
  );
}

export default ChangePassword;
